/**
 * O PRODUTO QUE A MAQUINA QUEIMA, como valor inicial do lancamento (achado dos
 * robos no QAS, 20/10/2026): o celular abria o abastecimento no primeiro
 * produto em ordem alfabetica — ALCOOL numa escavadeira —, a lista de tanques
 * vinha "Nenhum tanque com este produto" e o "Lancar 1,5 L" nao lancava.
 *
 * A FICHA MANDA (pedido do dono, 30/10/2026): "hoje na ficha do veiculo ja
 * informamos se ele e diesel ou gasolina [...] nos postos externos isso nao
 * acontece; para saber o tipo de combustivel devemos olhar para a ficha
 * tecnica do veiculo". Ate aqui o ultimo abastecimento vencia a ficha, e a
 * caminhonete a gasolina que um dia recebeu diesel por engano continuava
 * abrindo em diesel. Agora:
 *
 * 1. o combustivel da ficha (0060: diesel, gasolina, etanol, flex, gnv)
 *    RECORTA os candidatos, casados pelo nome do produto;
 * 2. entre eles, o do ultimo abastecimento de combustivel desta maquina
 *    desempata (DIESEL S10 ou S500, para a ficha "diesel"); depois o que tem
 *    tanque; depois o primeiro;
 * 3. sem ficha, ou com ficha que nenhum produto casa: o ultimo abastecimento;
 * 4. depois o primeiro combustivel que tem tanque ou comboio, o primeiro
 *    combustivel, e so entao o primeiro da lista.
 *
 * E so o VALOR INICIAL: quem lanca troca no campo, e a troca vale.
 */

interface ProdutoEscolhivel {
  readonly id: string;
  readonly nome: string;
  readonly categoria: string;
}

interface MaquinaDoProduto {
  readonly combustivel?: string | null;
  readonly ultimoProdutoId?: string | null;
}

/** As palavras do nome do produto que casam com o combustivel da ficha. */
const NOMES_DO_COMBUSTIVEL: Readonly<Record<string, readonly string[]>> = {
  diesel: ['DIESEL'],
  gasolina: ['GASOLINA'],
  etanol: ['ETANOL', 'ALCOOL'],
  flex: ['GASOLINA', 'ETANOL', 'ALCOOL'],
  gnv: ['GNV'],
  eletrico: [],
};

function semAcento(texto: string): string {
  return texto.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase();
}

/** Os produtos de combustivel que o combustivel da ficha nomeia. Vazio sem ficha. */
export function produtosDaFicha(combustivel: string | null | undefined, produtos: readonly ProdutoEscolhivel[]): readonly ProdutoEscolhivel[] {
  const palavras = combustivel ? (NOMES_DO_COMBUSTIVEL[combustivel] ?? []) : [];
  if (palavras.length === 0) return [];
  return produtos.filter((p) => p.categoria === 'combustivel' && palavras.some((w) => semAcento(p.nome).includes(w)));
}

export function produtoDaMaquina(
  maquina: MaquinaDoProduto | null | undefined,
  produtos: readonly ProdutoEscolhivel[],
  tanques: readonly { readonly produtoId: string }[],
): string | null {
  if (produtos.length === 0) return null;
  const comTanque = (p: ProdutoEscolhivel) => tanques.some((t) => t.produtoId === p.id);
  const ultimo = maquina?.ultimoProdutoId ? produtos.find((p) => p.id === maquina.ultimoProdutoId) : undefined;

  const daFicha = produtosDaFicha(maquina?.combustivel, produtos);
  if (daFicha.length > 0) {
    const escolhido = daFicha.find((p) => p.id === ultimo?.id) ?? daFicha.find(comTanque) ?? daFicha[0];
    return escolhido?.id ?? null;
  }
  if (ultimo) return ultimo.id;

  const escolhido =
    produtos.find((p) => p.categoria === 'combustivel' && comTanque(p)) ??
    produtos.find((p) => p.categoria === 'combustivel') ??
    produtos[0];
  return escolhido?.id ?? null;
}

/**
 * De onde veio o produto que o formulario preencheu, para a ajuda do campo
 * dizer — campo respondido pelo sistema sem o porque ninguem confere.
 */
export function origemDoProdutoDaMaquina(
  maquina: MaquinaDoProduto | null | undefined,
  produtoId: string,
  produtos: readonly ProdutoEscolhivel[],
): 'ficha' | 'historico' | 'lista' {
  if (produtosDaFicha(maquina?.combustivel, produtos).some((p) => p.id === produtoId)) return 'ficha';
  if (maquina?.ultimoProdutoId === produtoId) return 'historico';
  return 'lista';
}
